import { includes } from "ramda";
import { isNotNil } from "ramda-adjunct";
import { useEffect } from "react";
import { GLOBAL_API_PROP_NAME } from "@sinch/core/config/globals";
import { captureError } from "@sinch/core/config/sentry";

const instances = new Map();
if (!window[GLOBAL_API_PROP_NAME]) {
  window[GLOBAL_API_PROP_NAME] = {};
}
/**
 * Registers or updates a global API module property on the `window` object dynamically
 * and ensures proper cleanup when the component using this hook unmounts.
 *
 * @param {string} moduleName - The name of the global API module to update or create.
 *   Represents the `window[GLOBAL_API_PROPERTY_NAME]` child key under which global properties are stored.
 *   Should be unique to avoid collisions between different modules.
 *
 * @param {Record<string, any>} moduleProps - An object containing key-value pairs to be added to the global module.
 *   Defines the attributes or properties to be registered under the given moduleName.
 *   The values should be consistent with the expected data shapes.
 * @param instanceId
 */
export const useGlobalApi = (moduleName: string, moduleProps: Record<string, any>, instanceId?: string) => {
  const currentInstanceId = moduleName + "/" + instanceId;

  useEffect(() => {
    // Detect genuine collisions at registration time, not during render. Running
    // this in render produced false positives: on a remount the new instance
    // rendered before the previous instance's cleanup ran, so the keys still
    // looked taken. By the time this effect runs, any unmounting instance has
    // already cleaned up, so only a real double-registration is reported.
    const moduleApi = window[GLOBAL_API_PROP_NAME]?.[moduleName];
    const isSomeModuleExists = Object.keys(moduleProps).some(
      (key: string) =>
        (isNotNil(moduleApi) && includes(key, Object.keys(moduleApi))) ||
        (instances.has(currentInstanceId) && includes(key, instances.get(currentInstanceId)))
    );

    if (isSomeModuleExists) {
      captureError(new Error(`Global api property for ${moduleName} already exists`));
    }

    if (isNotNil(instanceId)) {
      instances.set(moduleName + "/" + instanceId, moduleProps);
    }

    window[GLOBAL_API_PROP_NAME] = {
      ...window[GLOBAL_API_PROP_NAME],
      [moduleName]: isNotNil(instanceId)
        ? {
            getInstance: (instId: string) => instances.get(moduleName + "/" + instId),
          }
        : {
            ...window[GLOBAL_API_PROP_NAME][moduleName],
            ...moduleProps,
          },
    };
    return () => {
      Object.keys(moduleProps).forEach((key: string) => {
        delete window[GLOBAL_API_PROP_NAME][moduleName][key];
        instances.delete(currentInstanceId);
      });
    };
  }, [currentInstanceId, instanceId, moduleName, moduleProps]);
};
